import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert
} from '@mui/material';
import {
  People as PeopleIcon,
  LocalHospital as HospitalIcon,
  Healing as HealingIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { dashboardAPI } from '../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [recentActivities, setRecentActivities] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statsRes, chartsRes, activitiesRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getCharts(),
        dashboardAPI.getRecentActivities(5)
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
      if (chartsRes.data.success) {
        setCharts(chartsRes.data.data);
      }
      if (activitiesRes.data.success) {
        setRecentActivities(activitiesRes.data.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'warning';
      case 'Scheduled':
        return 'info';
      default:
        return 'default';
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color, onClick }) => (
    <Card
      className="stat-card"
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: 6 } : {},
        transition: 'all 0.3s ease'
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold" color={color}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.50`,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 32, color: color } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!stats) {
    return null;
  }

  const renderOverallStats = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Patients"
          value={stats.overall.patients}
          subtitle={`+${stats.month.patients} this month`}
          icon={<PeopleIcon />}
          color="primary.main"
          onClick={() => navigate('/patients')}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Visitations"
          value={stats.overall.visitations}
          subtitle={`${stats.today.visitations} today`}
          icon={<HospitalIcon />}
          color="info.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Treatments"
          value={stats.overall.treatments}
          subtitle={`${stats.today.treatments} today`}
          icon={<HealingIcon />}
          color="success.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats.overall.revenue)}
          subtitle={`${formatCurrency(stats.month.revenue)} this month`}
          icon={<MoneyIcon />}
          color="warning.main"
        />
      </Grid>

      {/* Treatment Status Cards */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Treatment Status Overview
          </Typography>
          <Box mt={2}>
            {stats.treatmentsByStatus.map((item, index) => (
              <Box key={index} mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">{item.status}</Typography>
                  <Chip label={item.count} color={getStatusColor(item.status)} size="small" />
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: 'grey.200',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${(item.count / stats.overall.treatments) * 100}%`,
                      height: '100%',
                      backgroundColor: getStatusColor(item.status) === 'success' ? 'success.main' :
                                     getStatusColor(item.status) === 'warning' ? 'warning.main' : 'info.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Grid>

      {/* Gender Distribution */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Patient Demographics
          </Typography>
          <Box mt={2}>
            {stats.patientsByGender.map((item, index) => (
              <Box key={index} mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">{item.gender}</Typography>
                  <Chip
                    label={`${item.count} (${((item.count / stats.overall.patients) * 100).toFixed(1)}%)`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: 'grey.200',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${(item.count / stats.overall.patients) * 100}%`,
                      height: '100%',
                      backgroundColor: 'primary.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Grid>

      {/* Top Medicines */}
      {charts && charts.topMedicines && (
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Prescribed Medicines
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Medicine Name</TableCell>
                    <TableCell align="right">Usage Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {charts.topMedicines.slice(0, 5).map((medicine, index) => (
                    <TableRow key={index}>
                      <TableCell>{medicine.name}</TableCell>
                      <TableCell align="right">
                        <Chip label={medicine.usageCount} color="primary" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      )}
    </Grid>
  );

  const renderTodayStats = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="New Patients Today"
          value={stats.today.patients}
          icon={<PeopleIcon />}
          color="primary.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Visitations Today"
          value={stats.today.visitations}
          icon={<HospitalIcon />}
          color="info.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Treatments Today"
          value={stats.today.treatments}
          icon={<HealingIcon />}
          color="success.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Revenue Today"
          value={formatCurrency(stats.today.revenue)}
          icon={<MoneyIcon />}
          color="warning.main"
        />
      </Grid>

      {/* Recent Activities */}
      {recentActivities && (
        <>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Patients
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Registered</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentActivities.recentPatients.map((patient) => (
                      <TableRow
                        key={patient.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/patient/${patient.id}`)}
                      >
                        <TableCell>{`${patient.firstName} ${patient.lastName}`}</TableCell>
                        <TableCell>{patient.phone}</TableCell>
                        <TableCell>{formatDate(patient.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Treatments
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentActivities.recentTreatments.map((treatment) => (
                      <TableRow
                        key={treatment.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/treatment/${treatment.id}`)}
                      >
                        <TableCell>
                          {treatment.patient ?
                            `${treatment.patient.firstName} ${treatment.patient.lastName}` :
                            'N/A'
                          }
                        </TableCell>
                        <TableCell>{treatment.treatmentType}</TableCell>
                        <TableCell>
                          <Chip
                            label={treatment.status}
                            color={getStatusColor(treatment.status)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </>
      )}
    </Grid>
  );

  const renderMonthStats = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="New Patients"
          value={stats.month.patients}
          subtitle="This month"
          icon={<PeopleIcon />}
          color="primary.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Visitations"
          value={stats.month.visitations}
          subtitle="This month"
          icon={<HospitalIcon />}
          color="info.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Treatments"
          value={stats.month.treatments}
          subtitle="This month"
          icon={<HealingIcon />}
          color="success.main"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Revenue"
          value={formatCurrency(stats.month.revenue)}
          subtitle="This month"
          icon={<MoneyIcon />}
          color="warning.main"
        />
      </Grid>

      {/* Treatment Types Distribution */}
      {charts && charts.treatmentTypes && (
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Treatment Types Breakdown
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Treatment Type</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    <TableCell align="right">Avg Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {charts.treatmentTypes.map((treatment, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <HealingIcon sx={{ mr: 1, color: 'success.main' }} />
                          {treatment.type}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={treatment.count} color="primary" size="small" />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(treatment.totalCost)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(treatment.totalCost / treatment.count)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      )}

      {/* Daily Trends */}
      {charts && charts.daily && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Daily Patient Registrations (This Month)
            </Typography>
            <Box mt={2}>
              {charts.daily.patients.length > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {charts.daily.patients.length} registration days this month
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No registrations this month
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      )}

      {charts && charts.daily && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Daily Revenue (This Month)
            </Typography>
            <Box mt={2}>
              {charts.daily.revenue.length > 0 ? (
                <>
                  <Typography variant="h4" color="warning.main" gutterBottom>
                    {formatCurrency(stats.month.revenue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Across {charts.daily.revenue.length} days
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No revenue this month
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back! Here's what's happening with your clinic today.
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Overall" />
          <Tab label="Today" />
          <Tab label="This Month" />
        </Tabs>
      </Box>

      {tabValue === 0 && renderOverallStats()}
      {tabValue === 1 && renderTodayStats()}
      {tabValue === 2 && renderMonthStats()}
    </Container>
  );
};

export default Dashboard;
